import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AuthContainer, 
  AuthHeader, 
  AuthTitle, 
  AuthSubtitle, 
  AuthCard, 
  TechGrid, 
  DataStream,
  FormGroup,
  Label,
  Input,
  Button,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  Logo,
  TechCircle
} from './AuthStyles';
import PhoneVerification from './PhoneVerification';
import authService from '../../services/authService';

const Auth = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [displayPhone, setDisplayPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const navigate = useNavigate();

  // Verificar se o usuário já está autenticado e redirecionar de acordo com os dados
  useEffect(() => {
    if (authService.isAuthenticated()) {
      const userData = authService.getUserData();
      
      // Verificar se o usuário já adicionou produtos
      if (userData.segmento) {
        // Se tem segmento (com ou sem produtos), vai para onboarding
        navigate('/onboarding');
      } else {
        // Se não tem segmento, vai para informações básicas
        navigate('/register');
      }
    }
  }, [navigate]);

  // Limpar mensagens de erro quando o usuário começa a digitar
  useEffect(() => {
    if (phoneNumber) {
      setError('');
    }
  }, [phoneNumber]);

  const formatDisplayPhone = (phone) => {
    // Remove todos os caracteres não numéricos
    const numbers = phone.replace(/\D/g, '');
    
    if (numbers.length === 0) return '';
    
    // Formata o número conforme vai sendo digitado
    if (numbers.length <= 2) {
      return `(${numbers}`;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const validatePhoneNumber = (phone) => {
    // Remove todos os caracteres não numéricos para validação
    const numbers = phone.replace(/\D/g, '');
    
    // Verifica se o número tem entre 10 e 11 dígitos (DDD + número)
    // 10 dígitos: telefone fixo ou celular sem o 9
    // 11 dígitos: celular com o 9 na frente
    return numbers.length >= 10 && numbers.length <= 11;
  };

  const formatPhoneNumber = (phone) => {
    // Remove todos os caracteres não numéricos
    const numbers = phone.replace(/\D/g, '');
    
    // Adiciona o prefixo 55 (Brasil) no início do número
    // Formato final: 5511987654321
    return '55' + numbers;
  };

  const handlePhoneChange = (e) => {
    // Obtém apenas os números do input
    const inputValue = e.target.value;
    const numbers = inputValue.replace(/\D/g, '');
    
    // Limita a 11 dígitos (DDD + número)
    if (numbers.length <= 11) {
      setPhoneNumber(numbers);
      setDisplayPhone(formatDisplayPhone(numbers));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar o número de telefone
    if (!phoneNumber) {
      setError('Por favor, digite seu número de telefone.');
      return;
    }
    
    if (!validatePhoneNumber(phoneNumber)) {
      setError('Número de telefone inválido. Digite apenas o DDD e o número, sem o prefixo do país.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Formatar o número de telefone antes de enviar
      const formattedPhone = formatPhoneNumber(phoneNumber);
      
      // Enviar requisição para o webhook usando o serviço
      const response = await authService.requestVerificationCode(formattedPhone);
      
      // Verificar a resposta
      if (response === 'sucesso') {
        setSuccess('Código de verificação enviado para seu WhatsApp!');
        // Armazenar o número de telefone para uso posterior
        localStorage.setItem('userPhone', formattedPhone);
        // Mostrar o componente de verificação
        setShowVerification(true);
      } else {
        setError('Não foi possível enviar o código. Verifique seu número e tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao enviar número de telefone:', err);
      setError('Ocorreu um erro ao processar sua solicitação. Tente novamente mais tarde.');
    } finally {
      setIsLoading(false);
    }
  };

  // Se a verificação for bem-sucedida, redirecionar para a página de registro de usuário
  const handleVerificationSuccess = (userData) => {
    // Armazenar os dados do usuário usando o serviço
    authService.setUserData(userData);
    // Redirecionar para a página de registro de usuário
    navigate('/register');
  };

  return (
    <AuthContainer>
      <AuthHeader>
        <Logo src="/favicon.png" alt="BIANCA Logo" />
        <AuthTitle>BIANCA</AuthTitle>
        <AuthSubtitle>Bem-vindo à sua assistente virtual de atendimento</AuthSubtitle>
      </AuthHeader>
      
      <AuthCard>
        <TechGrid />
        <DataStream />
        <TechCircle size="200%" color="rgba(64, 128, 255, 0.2)" delay={0} />
        <TechCircle size="150%" color="rgba(64, 128, 255, 0.3)" delay={1} />
        
        {!showVerification ? (
          <>
            <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Acesse sua conta</h2>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="phone">Número de telefone</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(DDD) 98765-4321"
                  value={displayPhone}
                  onChange={handlePhoneChange}
                  disabled={isLoading}
                />
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}
              </FormGroup>
              
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <LoadingSpinner /> Enviando...
                  </>
                ) : (
                  'Continuar'
                )}
              </Button>
            </form>
          </>
        ) : (
          <PhoneVerification 
            phoneNumber={phoneNumber} 
            onSuccess={handleVerificationSuccess} 
          />
        )}
      </AuthCard>
    </AuthContainer>
  );
};

export default Auth;
